import { Box, Grid, List, Stack, styled } from '@mui/material';
import { FieldValues, FormProvider, SubmitHandler, useFormContext } from 'react-hook-form';
import { WsButton, WsDrawer } from 'wisestep-base';

import CheckboxFilter from '../../../../components/filters/components/CheckboxFilter';
import CheckboxListFilter from '../../../../components/filters/components/CheckboxListFilter';
import MultiMatchFilter from '../../../../components/filters/components/MultiMatchFilter';
import RadioListFilter from '../../../../components/filters/components/RadioListFilter';
import SalaryRangeFilter from '../../../../components/filters/components/SalaryRangeFilter';
import TextInputFilter from '../../../../components/filters/components/TextInputFilter';
import ExperienceRangeFilter from '../../components/ExperienceRangeFilter';
import BasicSearchForm from '../../types/IBasicSearchForm';
import DiceAdvanceSearch from '../types/IDiceAdvanceSearch';

interface AdvanceFilterProps {
  open: boolean;
  onClose: () => void;
  performSearch: (values: BasicSearchForm & DiceAdvanceSearch) => void;
}

const DrawerFooter = styled(Grid)(() => ({
  display: 'flex',
  alignItems: 'center',
  position: 'fixed',
  bottom: 1,
  left: 2,
  right: 2,
  justifyContent: 'flex-end',
}));

const AdvanceFilter = ({ open, onClose, performSearch }: AdvanceFilterProps) => {
  const methods = useFormContext();
  const watchField = !methods.watch('excludeSocialOnlyProfiles');
  const disabledInfoMessage =
    "Only available when the 'Exclude Social Only Profile' checkbox is selected";
  const { handleSubmit, reset } = methods;
  return (
    <FormProvider {...methods}>
      <WsDrawer
        title="Advanced Filters"
        open={open}
        onClose={() => onClose()}
        size="medium"
        headerSize="medium"
      >
        <Box
          component="form"
          role="presentation"
          onSubmit={handleSubmit(performSearch as SubmitHandler<FieldValues>)}
          onReset={() => reset()}
        >
          <Box pb={7}>
            <List>
              <ExperienceRangeFilter />
              <MultiMatchFilter
                name="skills.values"
                title="Skills"
                placeholder="Skills"
                matchTypeFieldName="skills.matchType"
              />
              <MultiMatchFilter
                name="jobTitle.values"
                title="Job Title"
                placeholder="Job Titles"
                matchTypeFieldName="jobTitle.matchType"
              />
              <MultiMatchFilter
                name="companies.values"
                title="Companies"
                placeholder="Companies"
                matchTypeFieldName="companies.matchType"
              />
              <CheckboxFilter
                name="excludeSocialOnlyProfiles"
                title="Profile Source"
                label="Exclude Social Only Profiles"
              />
              <CheckboxListFilter
                name="contactMethod"
                options={{
                  email: 'Email',
                  phone: 'Phone',
                }}
                title="Contact Method"
              />
              <RadioListFilter
                name="likelyToMove"
                row={false}
                options={[
                  { label: 'Any', value: -1 },
                  { label: 'Most Likely To Switch', value: 76 },
                  { label: 'Possibly Willing To Switch', value: 26 },
                  { label: 'Unlikely To Switch', value: 1 },
                  { label: 'Willing To Switch Unknown', value: 0 },
                ]}
                title="Likely To Switch"
              />
              <CheckboxListFilter
                name="education"
                options={{
                  associate: 'Associate',
                  bachelors: 'Bachelors',
                  'high school': 'High School',
                  masters: 'Masters',
                  mba: 'MBA',
                  'military service': 'Military Service',
                  'pre bachelors': 'Pre Bachelors',
                  'pre masters': 'Pre Masters',
                  'vocational school': 'Vocational School',
                }}
                title="Education"
                disabled={watchField}
                infoText={disabledInfoMessage}
              />
              <CheckboxListFilter
                name="languages"
                options={{
                  English: 'English',
                  Spanish: 'Spanish',
                  French: 'French',
                  German: 'German',
                  Russian: 'Russian',
                  Chinese: 'Chinese',
                  Italian: 'Italian',
                  Hindi: 'Hindi',
                  Portuguese: 'Portuguese',
                  Japanese: 'Japanese',
                  Arabic: 'Arabic',
                }}
                title="Languages"
              />
              <CheckboxListFilter
                name="additionalFilters"
                options={{
                  excludeThirdParty: 'Exclude Third Party',
                  excludeFounders: 'Exclude Founders',
                  hasPatent: 'Has Patent',
                }}
                title="Additional Filters"
              />
              <TextInputFilter
                name="resumeLastUpdated"
                placeholder="# of Days"
                title="Resume Last Updated"
                disabled={watchField}
                infoText={disabledInfoMessage}
              />
              <CheckboxListFilter
                name="employmentType"
                options={{
                  'full-time': 'Full Time',
                  'contract to hire - w2': 'Contract To Hire - W2',
                  'contract - w2': 'Contract - W2',
                  'contract - independent': 'Contract - Independent',
                  'contract to hire - independent': 'Contract To Hire - Independent',
                  'contract - corp-to-corp': 'Contract - Corp-To-Corp',
                  'contract to hire - corp-to-corp': 'Contract To Hire - Corp-To-Corp',
                  'part-time': 'Part-Time',
                }}
                title="Employment Type"
                disabled={watchField}
                infoText={disabledInfoMessage}
              />
              <CheckboxListFilter
                name="authorizedCountries"
                options={{
                  us: 'US',
                  uk: 'United Kingdom',
                  ca: 'Canada',
                  ie: 'Ireland',
                  in: 'India',
                  it: 'Italy',
                  es: 'Spain',
                  pl: 'Poland',
                  br: 'Brazil',
                  lt: 'Lithuania',
                  pt: 'Portugal',
                  ng: 'Nigeria',
                  au: 'Australia',
                  mx: 'Mexico',
                  hu: 'Hungary',
                }}
                title="Authorized Countries"
                disabled={watchField}
                infoText={disabledInfoMessage}
              />
              <CheckboxListFilter
                name="workAuthorization"
                options={{
                  'us citizenship': 'US Citizenship',
                  'green card': 'Green Card',
                  'have h1': 'Have H1',
                  'employment auth document': 'Employment Auth Document',
                  'need h1': 'Need H1',
                  'tn permit holder': 'Tn Permit Holder',
                }}
                title="Work Authorization"
                disabled={watchField}
                infoText={disabledInfoMessage}
              />
              <SalaryRangeFilter
                title="Compensation"
                selectArray={[
                  { label: 'USD', value: 'USD' },
                  { label: 'GBP', value: 'GBP' },
                  { label: 'EUR', value: 'EUR' },
                  { label: 'CAD', value: 'CAD' },
                ]}
                selectArray1={[
                  { label: 'Yearly', value: 'yearly' },
                  { label: 'Hourly', value: 'hourly' },
                ]}
                disabled={watchField}
                infoText={disabledInfoMessage}
              />
              <CheckboxFilter
                name="hasSecurityClearance"
                title="Has Security Clearance"
                label="Only with security clearance"
                disabled={watchField}
                infoText={disabledInfoMessage}
              />
              <CheckboxFilter
                name="willingToRelocate"
                title="Willing to Relocate"
                label="Willing To Relocate Anywhere"
                disabled={watchField}
                infoText={disabledInfoMessage}
              />
              <CheckboxFilter
                name="willingToRelocatePreferredLocationsOnly"
                title="Willing To Relocate Preferred Locations Only"
                label="Willing To Relocate Preferred Locations Only"
              />
              <CheckboxFilter
                name="willingToRelocateIncludeLocals"
                title="Willing To Relocate Include Locals"
                label="Willing To Relocate Include Locals"
              />
              <RadioListFilter
                name="travelPreference"
                row={false}
                options={[
                  { label: 'Any', value: '-1' },
                  { label: 'Upto 25%', value: '25' },
                  { label: 'Upto 50%', value: '50' },
                  { label: 'Upto 75%', value: '75' },
                  { label: 'Upto 100%', value: '100' },
                  { label: 'Prefers No Travel', value: '0' },
                ]}
                title="Travel Preference"
                disabled={watchField}
                infoText={disabledInfoMessage}
              />
            </List>
          </Box>
          <DrawerFooter>
            <Stack p={1} spacing={2} direction="row">
              <WsButton variant="outlined" size="large" type="reset">
                Reset
              </WsButton>
              <WsButton variant="contained" size="large" type="submit">
                Apply
              </WsButton>
            </Stack>
          </DrawerFooter>
        </Box>
      </WsDrawer>
    </FormProvider>
  );
};

export default AdvanceFilter;
